import { useCallback, useState, useEffect } from "react";
import {
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  User,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  solicitacaoAberturaCaixaService,
  solicitacaoReaberturaCaixaService,
} from "../../services/apiService";
import { formatarData, formatarDataDaAPI } from "../../utils/FormatarData";
import PageContainer from "../../components/PageContainer";

const SolicitacaoCaixa = () => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState("PENDENTE");
  const [tipoSolicitacao, setTipoSolicitacao] = useState("REABERTURA");

  // Estado da paginação
  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });

  // Reset página quando aplicar novos filtros
  useEffect(() => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  }, [filtroStatus, tipoSolicitacao]);

  const loadSolicitacoes = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (tipoSolicitacao === "REABERTURA") {
        data =
          await solicitacaoReaberturaCaixaService.listarRequisicaoReabertura(
            filtroStatus
          );
      } else {
        data = await solicitacaoAberturaCaixaService.listarRequisicaoAbertura(
          filtroStatus
        );
      }
      setSolicitacoes(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [filtroStatus, tipoSolicitacao]);

  useEffect(() => {
    loadSolicitacoes();
  }, [loadSolicitacoes]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "APROVADO":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "NEGADO":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "APROVADO":
        return "Aprovado";
      case "NEGADO":
        return "Negado";
      default:
        return "Pendente";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APROVADO":
        return "bg-green-100 text-green-800";
      case "NEGADO":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  // Cálculos de paginação
  const totalRegistros = solicitacoes.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const solicitacoesPaginadas = solicitacoes.slice(indiceInicial, indiceFinal);

  // Funções de navegação
  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  // Gerar números das páginas para exibição
  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    // Ajustar início se estivermos no final
    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Minhas Solicitações de Caixa
        </h1>
        <p className="text-gray-600">
          Visualize e gerencie suas solicitações de abertura e reabertura de
          caixa
        </p>
      </div>

      <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Solicitação
            </label>
            <select
              value={tipoSolicitacao}
              onChange={(e) => setTipoSolicitacao(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="REABERTURA">Reabertura</option>
              <option value="ABERTURA">Abertura</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por Status
            </label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="PENDENTE">Pendentes</option>
              <option value="APROVADO">Aprovadas</option>
              <option value="NEGADO">Negadas</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Solicitações de{" "}
            {tipoSolicitacao === "REABERTURA" ? "Reabertura" : "Abertura"} -{" "}
            {getStatusLabel(filtroStatus)}
          </h3>
          <span className="text-sm text-gray-500">
            {totalRegistros} registro(s) encontrado(s)
          </span>
        </div>

        {loading && solicitacoes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Carregando solicitações...
          </div>
        ) : solicitacoes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhuma solicitação encontrada para o status selecionado
          </div>
        ) : (
          <>
            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {tipoSolicitacao === "REABERTURA" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data do Caixa
                      </th>
                    )}
                    {tipoSolicitacao === "REABERTURA" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valor do Caixa
                      </th>
                    )}
                    {tipoSolicitacao === "ABERTURA" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data da Resposta
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    {tipoSolicitacao === "REABERTURA" ? (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Resposta
                      </th>
                    ) : (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Administrador
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Motivo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {solicitacoesPaginadas.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      {tipoSolicitacao === "REABERTURA" && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            <div className="text-sm font-medium text-gray-900">
                              {formatarData(item.caixa?.data)}
                            </div>
                          </div>
                        </td>
                      )}
                      {tipoSolicitacao === "REABERTURA" && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(item.caixa?.valorTotal)}
                            </span>
                          </div>
                        </td>
                      )}
                      {tipoSolicitacao === "ABERTURA" && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.dataResposta != null ? (
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              <div className="text-sm font-medium text-gray-900">
                                {formatarDataDaAPI(item.dataResposta)}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <div className="text-sm text-gray-500 italic">
                                Aguardando resposta...
                              </div>
                            </div>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            item.status
                          )}`}
                        >
                          {getStatusIcon(item.status)}
                          <span className="ml-1">
                            {getStatusLabel(item.status)}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {item.status === "PENDENTE" ? (
                            <span className="text-gray-500 italic">
                              Aguardando resposta...
                            </span>
                          ) : (
                            <div>
                              <div className="flex items-center mb-1">
                                <User className="h-3 w-3 text-gray-400 mr-1" />
                                <span className="text-xs text-gray-500">
                                  {item.administrador?.nome || "Admin"}
                                </span>
                              </div>
                              {tipoSolicitacao !== "ABERTURA" && (
                                <div className="text-xs text-gray-500">
                                  {item.dataResposta != null
                                    ? formatarData(item.dataResposta)
                                    : ""}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs">
                          <div className="truncate" title={item.motivo}>
                            {item.motivo}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Controles de Paginação */}
            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} até{" "}
                    {Math.min(indiceFinal, totalRegistros)} de {totalRegistros}{" "}
                    registros
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Primeira página */}
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    {/* Página anterior */}
                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {/* Números das páginas */}
                    <div className="flex space-x-1">
                      {gerarNumerosPaginas().map((numeroPagina) => (
                        <button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            paginacao.paginaAtual === numeroPagina
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {numeroPagina}
                        </button>
                      ))}
                    </div>

                    {/* Próxima página */}
                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    {/* Última página */}
                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default SolicitacaoCaixa;
